import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../shared/context/AuthContext";
import { useTheme } from "../../../shared/context/ThemeContext";
import { Button } from "../../../shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../shared/components/ui/dropdown-menu";
import { Badge } from "../../../shared/components/ui/badge";
import { cn, ROLE_LABELS } from "../../../shared/lib/utils";
import {
  Menu,
  Sun,
  Moon,
  LogOut,
  User,
  History,
  Activity,
  LayoutDashboard,
  Shield,
  HelpCircle,
} from "lucide-react";

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isSupervisor } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const isAdmin = user?.role === "admin";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Don't show header on login page
  if (location.pathname === "/login") {
    return null;
  }

  return (
    <header
      className="border-b-2 border-border bg-background sticky top-0 z-40"
      data-testid="app-header"
    >
      <div className="flex items-center justify-between h-16 px-4 md:px-6">
        {/* Logo */}
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate("/")}
          data-testid="logo"
        >
          <img 
            src="/logo.png" 
            alt="Station Log Book" 
            className="h-10 w-auto"
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-2">
          <Button
            variant={location.pathname === "/" ? "secondary" : "ghost"}
            onClick={() => navigate("/")}
            className="rounded-none font-mono text-xs uppercase tracking-wider"
          >
            <LayoutDashboard className="h-4 w-4 mr-2" />
            Dashboard
          </Button>
          <Button
            variant={location.pathname === "/history" ? "secondary" : "ghost"}
            onClick={() => navigate("/history")}
            className="rounded-none font-mono text-xs uppercase tracking-wider"
          >
            <History className="h-4 w-4 mr-2" />
            History
          </Button>
          {isSupervisor() && (
            <Button
              variant={
                location.pathname === "/supervisor" ? "secondary" : "ghost"
              }
              onClick={() => navigate("/supervisor")}
              className="rounded-none font-mono text-xs uppercase tracking-wider"
            >
              <Activity className="h-4 w-4 mr-2" />
              Supervisor
            </Button>
          )}
          {isAdmin && (
            <Button
              variant={
                location.pathname.startsWith("/admin") ? "secondary" : "ghost"
              }
              onClick={() => navigate("/admin")}
              className="rounded-none font-mono text-xs uppercase tracking-wider"
            >
              <Shield className="h-4 w-4 mr-2" />
              Admin
            </Button>
          )}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-none h-9 w-9"
            data-testid="theme-toggle"
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-none border-2 font-mono text-xs gap-2"
                data-testid="user-menu-trigger"
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">{user?.name}</span>
                <Badge
                  variant="outline"
                  className="rounded-none text-[10px] hidden sm:inline"
                >
                  {ROLE_LABELS[user?.role] || user?.role}
                </Badge>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none w-48">
              <div className="px-2 py-2 border-b border-border">
                <p className="font-mono text-sm font-bold">{user?.name}</p>
                <p className="font-mono text-xs text-muted-foreground">
                  ID: {user?.employee_id}
                </p>
              </div>
              <DropdownMenuItem
                onClick={() => navigate("/history")}
                className="rounded-none font-mono text-xs cursor-pointer"
              >
                <History className="h-4 w-4 mr-2" />
                My History
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem
                  onClick={() => navigate("/admin")}
                  className="rounded-none font-mono text-xs cursor-pointer"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => navigate("/manual")}
                className="rounded-none font-mono text-xs cursor-pointer"
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                User Manual
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="rounded-none font-mono text-xs cursor-pointer text-red-500"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-none h-9 w-9"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none w-48">
              <DropdownMenuItem
                onClick={() => navigate("/")}
                className="rounded-none font-mono text-xs cursor-pointer"
              >
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/history")}
                className="rounded-none font-mono text-xs cursor-pointer"
              >
                <History className="h-4 w-4 mr-2" />
                History
              </DropdownMenuItem>
              {isSupervisor() && (
                <DropdownMenuItem
                  onClick={() => navigate("/supervisor")}
                  className="rounded-none font-mono text-xs cursor-pointer"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  Supervisor
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem
                  onClick={() => navigate("/admin")}
                  className="rounded-none font-mono text-xs cursor-pointer"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSupervisor } = useAuth();

  const isAdmin = user?.role === "admin";

  // Don't show on login page
  if (location.pathname === "/login") {
    return null;
  }

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 border-t-2 border-border bg-background p-2 z-50 md:hidden"
      data-testid="mobile-nav"
    >
      <div className={`grid ${isAdmin ? "grid-cols-4" : "grid-cols-3"} gap-2`}>
        <Button
          variant={location.pathname === "/" ? "secondary" : "ghost"}
          onClick={() => navigate("/")}
          className="rounded-none h-12 flex-col gap-1 font-mono text-[10px] uppercase tracking-wider"
        >
          <LayoutDashboard className="h-5 w-5" />
          Home
        </Button>
        <Button
          variant={location.pathname === "/history" ? "secondary" : "ghost"}
          onClick={() => navigate("/history")}
          className="rounded-none h-12 flex-col gap-1 font-mono text-[10px] uppercase tracking-wider"
        >
          <History className="h-5 w-5" />
          History
        </Button>
        {isSupervisor() ? (
          <Button
            variant={location.pathname === "/supervisor" ? "secondary" : "ghost"}
            onClick={() => navigate("/supervisor")}
            className="rounded-none h-12 flex-col gap-1 font-mono text-[10px] uppercase tracking-wider"
          >
            <Activity className="h-5 w-5" />
            Supervisor
          </Button>
        ) : (
          <Button
            variant="ghost"
            onClick={() => navigate("/history")}
            className="rounded-none h-12 flex-col gap-1 font-mono text-[10px] uppercase tracking-wider"
          >
            <User className="h-5 w-5" />
            Profile
          </Button>
        )}
        {isAdmin && (
          <Button
            variant={location.pathname.startsWith("/admin") ? "secondary" : "ghost"}
            onClick={() => navigate("/admin")}
            className="rounded-none h-12 flex-col gap-1 font-mono text-[10px] uppercase tracking-wider"
          >
            <Shield className="h-5 w-5" />
            Admin
          </Button>
        )}
      </div>
    </nav>
  );
};
